import { useState, useEffect, useCallback, MouseEvent } from 'react';

const hotelImages = [
  {
    src: 'https://storage.readdy-site.link/project_files/ab967d20-2987-4048-a339-d91f8ceb0be3/bfc76b2d-33f6-414c-bd15-e27c1f552242_2fad889f11ebdd41f5f80cb8d3e9b896.jpg?v=96dd10b9a420817ae2c3d7836247d0f7',
    alt: 'Hotel suite luxe — lumière naturelle matinale'
  },
  {
    src: 'https://storage.readdy-site.link/project_files/ab967d20-2987-4048-a339-d91f8ceb0be3/cf8804a0-3e02-43df-bc65-2f7d9db978c8_a57d490429cf74cbcf3f7f80613c774d.jpg?v=7106f622034413b86bf1cb4161739b86',
    alt: 'Chambre hôtel — vue luxe'
  },
  {
    src: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=85&fit=crop',
    alt: 'Lobby hôtel palace — lumière ambiante'
  },
  {
    src: 'https://storage.readdy-site.link/project_files/ab967d20-2987-4048-a339-d91f8ceb0be3/c9581531-b6db-48a2-9706-5cd275427d78_6a0aaa0ada7896bb91ff384be991ba6f.jpg?v=1e666d3dcf624715ee12fc8057f306f8',
    alt: 'Vue piscine hôtel — coucher de soleil'
  },
];

const restaurantImages = [
  {
    src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=85&fit=crop',
    alt: 'Salle de restaurant gastronomique'
  },
  {
    src: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=85&fit=crop',
    alt: 'Table dressée — lumière dorée'
  },
  {
    src: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&q=85&fit=crop',
    alt: 'Plat gastronomique — close-up texture'
  },
  {
    src: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200&q=85&fit=crop',
    alt: 'Bar à vin — ambiance feutrée'
  },
];

interface ImageSliderProps {
  images: { src: string; alt: string }[];
  autoPlayInterval?: number;
}

function ImageSlider({ images, autoPlayInterval = 4000 }: ImageSliderProps) {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % images.length);
  }, [images.length]);

  const prev = () => {
    setCurrent((c) => (c - 1 + images.length) % images.length);
  };

  useEffect(() => {
    const timer = setInterval(next, autoPlayInterval);
    return () => clearInterval(timer);
  }, [next, autoPlayInterval]);

  return (
    <div className="relative w-full h-full overflow-hidden group">
      {images.map((img, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={img.src}
            alt={img.alt}
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
        </div>
      ))}

      {/* Flèche gauche */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white text-gray-800 opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer"
        aria-label="Previous image"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>

      {/* Flèche droite */}
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white text-gray-800 opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer"
        aria-label="Next image"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`transition-all duration-300 cursor-pointer rounded-full ${
              i === current
                ? 'w-5 h-1.5 bg-white'
                : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>

      {/* Compteur */}
      <div className="absolute top-4 right-4 font-['Montserrat'] text-xs text-white/80 tracking-wider">
        {String(current + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
      </div>
    </div>
  );
}

export function HospitalitySection() {
  const handleContactClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="hospitality-section" className="bg-white">

      {/* Header */}
      <div className="py-16 md:py-24 px-4 md:px-6 text-center">
        <h3 className="text-xs md:text-sm uppercase tracking-widest text-gray-400 mb-4">
          Hospitality
        </h3>
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-serif font-light text-gray-900 mb-6">
          Hotels &amp; Restaurants
        </h2>
        <div className="w-px h-14 bg-gray-300 mx-auto mb-6"></div>
        <p className="text-sm md:text-base text-gray-600 font-serif italic max-w-2xl mx-auto leading-relaxed">
          We bring to hospitality the same visual precision and editorial refinement —
          crafting imagery that elevates your establishment and seduces discerning guests.
        </p>
      </div>

      {/* Hotel — slider gauche, texte droite */}
      <div id="hotel-section" className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative h-[480px] md:h-[600px] lg:h-[700px]">
          <ImageSlider images={hotelImages} autoPlayInterval={4500} />
        </div>

        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-16 bg-gray-50">
          <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-6">
            01 — Hotel
          </h3>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-gray-900 mb-6 leading-tight">
            Rooms that<br />speak for themselves
          </h2>
          <div className="w-8 h-px bg-gray-400 mb-8"></div>
          <p className="text-sm md:text-base leading-relaxed text-gray-600 mb-6 font-serif">
            From intimate boutique hotels to grand Parisian palaces, we produce photography that captures the soul of each space — the soft morning light through linen curtains, the texture of a polished marble counter, the silence of an empty lobby.
          </p>
          <p className="text-sm leading-relaxed text-gray-500 mb-10 font-serif italic">
            Interiors, architecture, room details, common areas, rooftop terraces — every frame is composed to convert admiration into reservation.
          </p>
          <a
            href="#contact-section"
            onClick={handleContactClick}
            className="inline-block self-start px-8 py-3 border border-gray-800 text-gray-900 text-xs uppercase tracking-wider hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Request a quote
          </a>
        </div>
      </div>

      {/* Restaurant — texte gauche, slider droite */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-16 bg-white order-2 lg:order-1">
          <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-6">
            02 — Restaurant
          </h3>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-gray-900 mb-6 leading-tight">
            Cuisine elevated<br />to its rightful stage
          </h2>
          <div className="w-8 h-px bg-gray-400 mb-8"></div>
          <p className="text-sm md:text-base leading-relaxed text-gray-600 mb-6 font-serif">
            We photograph gastronomy the way a chef plates a dish — with intention. Ambient dining rooms at golden hour, close-up textures of a signature dessert, the choreography of a service in full motion.
          </p>
          <p className="text-sm leading-relaxed text-gray-500 mb-10 font-serif italic">
            Whether you run a starred restaurant, a wine bar, or a rooftop brasserie, we create imagery that draws guests to the table before the first bite.
          </p>
          <a
            href="#contact-section"
            onClick={handleContactClick}
            className="inline-block self-start px-8 py-3 border border-gray-800 text-gray-900 text-xs uppercase tracking-wider hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Request a quote
          </a>
        </div>

        <div className="relative h-[480px] md:h-[600px] lg:h-[700px] order-1 lg:order-2">
          <ImageSlider images={restaurantImages} autoPlayInterval={5000} />
        </div>
      </div>

      {/* Separator discret */}
      <div className="h-px bg-gray-100 mx-6 md:mx-20"></div>

    </section>
  );
}
